import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import logoImage from '../assets/logo.png';
import { authJsonHeaders } from '../utils/authHeaders';

function formatMontant(montant) {
    const value = parseFloat(montant);
    if (Number.isNaN(value)) return '0,000 TND';
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const montantTronque = Math.floor(absValue * 1000 + 0.0000001) / 1000;
    const tempStr = montantTronque.toFixed(10);
    const parts = tempStr.split('.');
    let integerPart = parts[0] || '0';
    let decimalPart = parts[1] ? parts[1].substring(0, 3) : '000';
    if (decimalPart.length < 3) decimalPart = decimalPart.padEnd(3, '0');
    const integerFormatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${isNegative ? '-' : ''}${integerFormatted},${decimalPart} TND`;
}

function ligneFondMontant(c) {
    return Math.max(parseFloat(c.avance || 0), parseFloat(c.montant || 0));
}

function periodLabelFromParams(p) {
    const { filterType, date, mois, annee, dateDebut, dateFin } = p;
    if (filterType === 'jour' && date) return `Jour : ${date}`;
    if (filterType === 'periode' && dateDebut && dateFin) return `Période : ${dateDebut} – ${dateFin}`;
    if (filterType === 'mois' && mois && annee) return `Mois : ${String(mois).padStart(2, '0')}/${annee}`;
    if (filterType === 'annee' && annee) return `Année : ${annee}`;
    return '';
}

/** Même logique que l'état : par client, reste = total argent chez CGM − total payé par fond (période complète). */
function buildClientsRestePositif(fondsArr, payesArr) {
    const map = new Map();
    for (const c of fondsArr || []) {
        const id = c.client_id;
        if (id == null) continue;
        if (!map.has(id)) {
            map.set(id, {
                client_id: id,
                client_nom: c.client_nom || '',
                client_prenom: c.client_prenom || '',
                fonds: 0,
                paye: 0,
            });
        }
        map.get(id).fonds += ligneFondMontant(c);
    }
    for (const c of payesArr || []) {
        const id = c.client_id;
        if (id == null) continue;
        if (!map.has(id)) {
            map.set(id, {
                client_id: id,
                client_nom: c.client_nom || '',
                client_prenom: c.client_prenom || '',
                fonds: 0,
                paye: 0,
            });
        }
        map.get(id).paye += parseFloat(c.montant || 0);
    }
    return [...map.values()]
        .map((row) => {
            const reste = row.fonds - row.paye;
            return { ...row, reste };
        })
        .filter((row) => row.reste > 0.0005)
        .sort((a, b) => {
            const na = `${a.client_nom} ${a.client_prenom}`.trim().toLocaleLowerCase();
            const nb = `${b.client_nom} ${b.client_prenom}`.trim().toLocaleLowerCase();
            return na.localeCompare(nb, 'fr');
        });
}

export default function FondClientCgmEtatViewPage() {
    const [searchParams] = useSearchParams();
    const [fonds, setFonds] = useState([]);
    const [payes, setPayes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [clientSearch, setClientSearch] = useState('');
    const queryParts = useMemo(() => {
        const filterType = searchParams.get('filterType');
        return {
            filterType,
            date: searchParams.get('date'),
            mois: searchParams.get('mois'),
            annee: searchParams.get('annee'),
            dateDebut: searchParams.get('dateDebut'),
            dateFin: searchParams.get('dateFin'),
        };
    }, [searchParams]);

    useEffect(() => {
        const { filterType, date, mois, annee, dateDebut, dateFin } = queryParts;
        const params = new URLSearchParams();
        if (filterType === 'jour' && date) {
            params.set('date', date);
        } else if (filterType === 'periode' && dateDebut && dateFin) {
            params.set('dateDebut', dateDebut);
            params.set('dateFin', dateFin);
        } else if (filterType === 'mois' && mois && annee) {
            params.set('mois', mois);
            params.set('annee', annee);
        } else if (filterType === 'annee' && annee) {
            params.set('annee', annee);
        } else {
            setError('Paramètres de période manquants ou invalides.');
            setLoading(false);
            setFonds([]);
            setPayes([]);
            return;
        }

        const qs = params.toString();
        let cancelled = false;
        setLoading(true);
        setError('');

        (async () => {
            try {
                const [r1, r2] = await Promise.all([
                    fetch(`/api/charges-mensuelles/fond-client-cgm?${qs}`, { headers: authJsonHeaders() }),
                    fetch(`/api/charges-mensuelles/paye-par-fond-client?${qs}`, { headers: authJsonHeaders() }),
                ]);
                const d1 = await r1.json();
                const d2 = await r2.json();
                if (cancelled) return;
                if (!d1.success) {
                    setError(d1.error || 'Erreur chargement des fonds');
                    setFonds([]);
                    setPayes([]);
                    return;
                }
                if (!d2.success) {
                    setError(d2.error || 'Erreur chargement des paiements');
                    setFonds([]);
                    setPayes([]);
                    return;
                }
                setFonds(d1.charges || []);
                setPayes(d2.charges || []);
            } catch (e) {
                if (!cancelled) {
                    setError(e.message || 'Erreur réseau');
                    setFonds([]);
                    setPayes([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [queryParts]);

    const searchNorm = clientSearch.trim().toLowerCase();
    const matchClient = (nom, prenom) => {
        if (!searchNorm) return true;
        const full = `${nom || ''} ${prenom || ''}`.trim().toLowerCase();
        return full.includes(searchNorm);
    };

    const clientsRestePositif = useMemo(
        () => buildClientsRestePositif(fonds, payes),
        [fonds, payes]
    );
    const clientsResteAffiche = useMemo(
        () => clientsRestePositif.filter((row) => matchClient(row.client_nom, row.client_prenom)),
        [clientsRestePositif, clientSearch]
    );
    const sommeArgentChezNous = clientsResteAffiche.reduce((t, r) => t + r.reste, 0);

    const periodeText = periodLabelFromParams(queryParts);
    const now = new Date();
    const dateImpression = now.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
    const heureImpression = now.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
    });

    return (
        <>
            <style>{`
                .fce-toolbar { background: #f8f9fa; border-bottom: 1px solid #dee2e6; padding: 10px 16px; display: flex; flex-wrap: wrap; gap: 10px; align-items: center; justify-content: space-between; }
                .fce-sheet { max-width: 1000px; margin: 0 auto; padding: 16px 20px 32px; background: #fff; min-height: calc(100vh - 52px); }
                .fce-letterhead { margin-bottom: 16px; }
                .fce-date-impression { text-align: right; font-size: 11px; font-weight: 700; margin-bottom: 10px; color: #000; }
                .fce-letterhead-center { text-align: center; }
                .fce-letterhead-logo-wrap { margin-bottom: 6px; }
                .fce-letterhead-logo { height: 50px; width: auto; vertical-align: middle; }
                .fce-letterhead-cols {
                    display: flex; justify-content: space-between; align-items: flex-start;
                    margin-top: 6px; font-size: 9px; line-height: 1.35;
                    font-family: Arial, sans-serif; color: #000;
                }
                .fce-letterhead-col-left { width: 48%; text-align: left; }
                .fce-letterhead-col-right { width: 48%; text-align: right; }
                .fce-head-rule { border: 0; border-top: 1px solid #000; margin: 12px 0 14px; }
                .fce-doc-title-block { text-align: center; padding-bottom: 4px; margin-bottom: 12px; border-bottom: 1px solid #ccc; }
                .fce-title { font-size: 1.1rem; font-weight: 700; margin: 6px 0 4px; }
                .fce-meta { font-size: 0.8rem; color: #555; }
                .fce-h6 { font-size: 0.78rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; margin: 16px 0 8px; }
                .fce-table-wrap { overflow-x: auto; }
                .fce-table { width: 100%; border-collapse: collapse; font-size: 11px; font-family: Arial, sans-serif; }
                .fce-table th, .fce-table td { border: 1px solid #000; padding: 5px 8px; vertical-align: middle; }
                .fce-table thead th { background: #f9f9f9; font-weight: 700; }
                .fce-total { font-weight: 700; background: #f9f9f9; }
                @media print {
                    .no-print { display: none !important; }
                    .fce-toolbar { display: none !important; }
                    .fce-sheet { padding: 8px 12px; min-height: auto; }
                    body { background: #fff !important; }
                }
            `}</style>

            <div className="fce-toolbar no-print">
                <div className="d-flex flex-wrap gap-2 align-items-center">
                    <Link to="/fond-client-cgm" className="btn btn-outline-secondary btn-sm">
                        <i className="fas fa-arrow-left me-1"></i>Retour
                    </Link>
                    <span className="text-muted small d-none d-sm-inline">État prêt pour impression ou export PDF</span>
                </div>
                <button type="button" className="btn btn-dark btn-sm" onClick={() => window.print()}>
                    <i className="fas fa-print me-1"></i>Imprimer / PDF
                </button>
            </div>

            <div className="fce-sheet">
                <header className="fce-letterhead">
                    <div className="fce-date-impression">
                        Date d&apos;impression : {dateImpression}, {heureImpression}
                    </div>
                    <div className="fce-letterhead-center">
                        <div className="fce-letterhead-logo-wrap">
                            <img src={logoImage} alt="Logo CGM" className="fce-letterhead-logo" />
                        </div>
                        <div className="fce-letterhead-cols">
                            <div className="fce-letterhead-col-left">
                                Matricule fiscale : 1408941/H/A/P/000
                                <br />
                                Banque Zitouna Agence Ben Arous
                                <br />
                                RIB : 25047000000039870129
                            </div>
                            <div className="fce-letterhead-col-right">
                                Membre de la compagnie des comptables de Tunisie.
                                <br />
                                62 Avenue de France, bureau N°3, 1er étage 2013 Ben Arous.
                                <br />
                                (+216) 79 410 028
                                <br />
                                contact@ccgm.com.tn
                            </div>
                        </div>
                    </div>
                    <hr className="fce-head-rule" />
                    <div className="fce-doc-title-block">
                        <h1 className="fce-title mb-0">Fond Client Chez CGM</h1>
                        {periodeText && (
                            <p className="fce-meta mb-1 mt-2">
                                <strong>Période :</strong> {periodeText}
                            </p>
                        )}
                        <p className="fce-meta mb-0">
                            Affiché le : {dateImpression} {heureImpression}
                        </p>
                    </div>
                </header>

                <div className="mb-3 no-print" id="fce-search-block">
                    <label htmlFor="fce-search" className="form-label small text-muted mb-1">Rechercher par nom de client</label>
                    <input
                        id="fce-search"
                        type="search"
                        className="form-control form-control-sm"
                        placeholder="Nom, prénom ou partie du nom affiché dans la colonne Client…"
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        autoComplete="off"
                    />
                </div>

                {loading && (
                    <div className="text-center py-5 text-muted">Chargement…</div>
                )}
                {!loading && error && (
                    <div className="alert alert-danger">{error}</div>
                )}
                {!loading && !error && (
                    <>
                        <h2 className="fce-h6">Nom client et reste fond</h2>
                        <p className="small text-muted mb-2 no-print">
                            Seuls les clients dont le <strong>solde</strong> (total fonds client chez CGM − total payé par fond client sur la période) est
                            {' '}<strong>supérieur à 0</strong>. La recherche filtre les lignes affichées.
                        </p>
                        <div className="fce-table-wrap">
                            <table className="fce-table">
                                <thead>
                                    <tr>
                                        <th>Nom du client</th>
                                        <th className="text-end">Reste fond</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {clientsResteAffiche.length === 0 ? (
                                        <tr>
                                            <td colSpan={2} className="text-center text-muted py-3">
                                                {clientsRestePositif.length === 0
                                                    ? 'Aucun client avec un solde supérieur à 0 sur cette période.'
                                                    : 'Aucun résultat pour la recherche (essayez de vider le filtre).'}
                                            </td>
                                        </tr>
                                    ) : (
                                        clientsResteAffiche.map((row) => (
                                            <tr key={`r-${row.client_id}`}>
                                                <td>{`${row.client_nom || ''} ${row.client_prenom || ''}`.trim() || `Client #${row.client_id}`}</td>
                                                <td className="text-end text-success fw-bold">{formatMontant(row.reste)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                                {clientsResteAffiche.length > 0 && (
                                    <tfoot>
                                        <tr className="fce-total">
                                            <td className="text-end">Total reste fond (lignes affichées) :</td>
                                            <td className="text-end text-success">{formatMontant(sommeArgentChezNous)}</td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
